import { useLayoutEffect, useState } from 'react'

import { isAvatarSrc, socialBackgroundWithFallback, thumbnailMediumBlue } from '@utils/thumbnails'
import { useLocation, useNavigate } from 'react-router-dom'
import { colors, fontSize, size } from '@styles/theme'
import { useAuthContext } from '@contexts/authContext'
import useDrawerContext from '@contexts/drawerContext'
import useModalContext from '@contexts/modalContext'
import useDialog from '@hooks/useDialog'
import styled from '@emotion/styled'

import { ProfileBottomDrawerContents } from '@components/layout/bottom_drawers/ProfileBottomDrawerContents'
import { SocialNearbyProfileButton } from '@elements/buttons/SocialNearbyProfileButton'
import NearbySettingsDialog from '@forms/profile_forms/MainProfileForm'
import { SocialProfileTabs } from './elements/SocialProfileTabs'
import FloatingButton from '@elements/FloatingButton'
import { ButtonBase } from '@mui/material'
import FollowButton from './FollowButton'
import {
  PiCaretLeftBold,
  PiDotsThreeVerticalBold,
  PiGearSixBold,
} from 'react-icons/pi'

const TopBar = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  width: 100svw;
  z-index: 1000;
`

const TopbarMain = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;

  padding: 0 ${size(1)};
  width: 100%;
  
  border-bottom: 0.5px solid transparent;

  transition-duration: 300ms;
  overflow: hidden;
`

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 1;
  overflow: hidden;
  z-index: 1;
`

const UserName = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 ${size(0.25)} 0 ${size(2)};
  transition-duration: 300ms;
  overflow: hidden;
  flex-shrink: 1;
  text-shadow: 0 1px 4px #333333CC;
`
  
const DisplayName = styled.h2`
  font-size: ${fontSize('md')};
  margin: 0;
  padding: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`

const PostCount = styled.span`
  color: white;
  font-size: ${fontSize('sm')};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  text-shadow: 0 1px 4px #333333CC;
`

const RightSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0 ${size(1)};
  flex-shrink: 0;
  flex-grow: 1;
  z-index: 1;
`

const ButtonFloat = styled(FloatingButton)`
  padding: ${size(1)};
  height: auto;
  min-width: 0;
`

const FloatButton = styled(ButtonBase)`
  position: fixed;
  bottom: ${size(4)};
  right: ${size(1)};
  border-radius: 100%;
  box-shadow: 0px 2px 5px rgba(0,0,0, 0.35);
  background-color: ${colors.statusColors['right_now']('primary')};
  z-index: 2;
  height: ${size(7)};
  width: ${size(7)};
  display: flex;
  align-items: center;
  justify-content: center;
`

const FloatButtonIcon = styled.img`
  height: ${size(7)};
  width: ${size(7)};
`

const BlurredBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;

  width: 100%;
  height: 100%;
  
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;

  filter: blur(10px);
`

const SocialProfileTopbar = ({ followUser, unfollowUser, profile = {}, setProfile, isOwnProfile, selectedTab, setSelectedTab }) => {
  const { state: authState } = useAuthContext()
  const isAnonymous = authState?.user?.isAnonymous
  const { newPost } = useModalContext()
  const [isSettingsOpen, openSettings, closeSettings] = useDialog('social_profile_settings', 'social-profile-settings-open', { params: { form_display: 'social' } })
  const { nearbyProfile } = useModalContext()
  const [isAboveThreshold, setIsAboveThreshold] = useState(true)
  const [isAboveTabs, setIsAboveTabs] = useState(true)
  const { openBottomDrawer } = useDrawerContext()
  const navigate = useNavigate()
  const isNavigatedFromNearbyProfile = !!useLocation()?.state?.isNavigatedFromNearbyProfile
  const nearbySrc = thumbnailMediumBlue(profile?.nearbyMedia?.thumbnails?.[0], profile?.defaultAvatar)
  const isAvatar = isAvatarSrc('medium', profile?.nearbyMedia?.thumbnails?.[0], profile?.defaultAvatar)

  useLayoutEffect(() => {
    const topbarThresholdAnchor = document.querySelector('#social-profile_topbar-threshold')
    if (!topbarThresholdAnchor) return;
    const observer = new window.IntersectionObserver(([entry]) => {
      if (entry.boundingClientRect.top < 0) {
        // IS BELOW ANCHOR
        setIsAboveThreshold(false)
      } else {
        // IS ABOVE ANCHOR
        setIsAboveThreshold(true)
      }
    }, { threshold: 0 })

    setTimeout(() => {
      observer.observe(topbarThresholdAnchor)
    }, 10)

    // Cleanup state and observer
    return () => {
      setIsAboveThreshold(true)
      observer.unobserve(topbarThresholdAnchor)
    }
  }, [])

  useLayoutEffect(() => {
    const tabsThresholdAnchor = document.querySelector('#social-profile_tabs-threshold')
    if (!tabsThresholdAnchor) return;
    const observer = new window.IntersectionObserver(([entry]) => {
      if (entry.boundingClientRect.top < 0) {
        // IS BELOW ANCHOR
        setIsAboveTabs(false)
      } else {
        // IS ABOVE ANCHOR
        setIsAboveTabs(true)
      }
    }, { threshold: 0 })

    setTimeout(() => {
      observer.observe(tabsThresholdAnchor)
    }, 10)

    // Cleanup state and observer
    return () => {
      setIsAboveTabs(true)
      observer.unobserve(tabsThresholdAnchor)
    }
  }, [profile])

  const updateOwnUser = (updatedUser) => {
    if (isOwnProfile) setProfile(updatedUser)
  }

  const nearbyProfileNavigation = () => {
    // NOTE: We want to backnavigate if we navigated from the nearby profile
    // so we don't get stuck in looped backnavigation later
    if (isNavigatedFromNearbyProfile) {
      navigate(-1)
    } else {
      nearbyProfile.open({
        pathname: '/map',
        search: { nearby_uuid: profile?.nearbyUuid } },
        { state: { isNavigatedFromSocial: true } }
      )
    }
  }

  return (
    <>
      <TopBar>
        <NearbySettingsDialog
          isOpen={isSettingsOpen}
          setClosed={closeSettings}
          afterSubmit={updateOwnUser}
          setUser={setProfile}
        />
        <TopbarMain
          style={{
            backgroundColor: !isAboveThreshold && isAboveTabs && 'rgba(0, 0, 0, 0.9)',
            backdropFilter: !isAboveThreshold && 'blur(10px)',
            backgroundColor: !isAboveThreshold && 'rgba(20, 20, 20, 0.9)',
            height: !isAboveThreshold ? size(7.5) : size(7),
          }}>
          <BlurredBackground
            style={{
              backgroundImage: !isAboveThreshold && `url(${socialBackgroundWithFallback(profile)})`
            }}
          />
          <LeftSection>
            <ButtonFloat
              style={{ backgroundColor: !isAboveThreshold && '#333333', }}
              onClick={() => navigate(-1)}>
              <PiCaretLeftBold size={size(2.5)} />
            </ButtonFloat>
            <UserName style={{ opacity: isAboveThreshold ? 0 : 1 }}>
              <DisplayName>
                {profile?.displayName}
              </DisplayName>
              <PostCount>
                {profile?.postCount || 0} posts
              </PostCount>
            </UserName>
          </LeftSection>
          <RightSection>
            {!isOwnProfile &&
              <FollowButton
                profile={profile}
                styles={{
                  height: 'auto',
                  padding: `${size(1.1)} ${size(1.75)}`,
                  opacity: isAboveThreshold ? 0 : (isAnonymous ? 0.4 : 1),
                  transitionDuration: '300ms',
                  border: '0.5px solid rgba(255,255,255, 0.15)',
                }}
                unfollowUser={unfollowUser}
                disabled={isAboveThreshold}
                followUser={followUser}
              />
            }
            {isAboveThreshold &&
              <SocialNearbyProfileButton
                isAboveAnchor={isAboveThreshold}
                onNavigate={nearbyProfileNavigation}
                thumbnailSrc={nearbySrc}
                isAvatar={isAvatar}
                buttonText='Nearby'
              />
            }
            <ButtonFloat
              style={{ backgroundColor: !isAboveThreshold && '#333333', }}
              onClick={() => {
                if (isOwnProfile) {
                  openSettings() 
                } else {
                  openBottomDrawer(
                    <ProfileBottomDrawerContents
                      otherUser={profile}
                      isOnSocialProfile
                    />
                  )
                }
              }}>
              {isOwnProfile
                ? <PiGearSixBold size={size(2.5)} />
                : <PiDotsThreeVerticalBold size={size(2.5)} />
              }
            </ButtonFloat>
          </RightSection>
        </TopbarMain>
        {!isAboveTabs &&
          <SocialProfileTabs
            className='normal_opacity-slide-vertical'
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            // NOTE: minWidth styles are useful for the icon variant if desired
            // sx={{ '& .MuiTab-root': { minHeight: 0, minWidth: size(10), maxWidth: 'calc(100svw/4)' }}}
            sx={{ '& .MuiTab-root': { minHeight: 0, maxWidth: 'calc(100svw/4)' }}}
          />
        }
      </TopBar>
      <FloatButton onClick={() => newPost.open(!isOwnProfile && { search: { mention: profile?.handle } })}>
        {/* <PiPlusBold size={size(4)} /> */}
        <FloatButtonIcon src={'https://mandrake-app.b-cdn.net/dev/application/icons/plus-icon.svg'} />
      </FloatButton>
    </>
  )
}

export default SocialProfileTopbar
